import React, { useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';

const ContractPreviewModal = ({ isOpen, onClose, contract, contractType = 'regular' }) => {
  const printRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 100);
  };

  const handleDownloadPDF = () => {
    // Trigger print dialog which allows saving as PDF
    handlePrint();
  };

  const renderRegularContract = () => (
    <div className="contract-content" dir="rtl">
      {/* Header */}
      <div className="text-center mb-8 pb-6 border-b-2 border-gray-300">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">عقد {contract?.contractType || 'إيجار'}</h1>
        <p className="text-lg text-gray-600">رقم العقد: {contract?.contractNumber}</p>
        <p className="text-sm text-gray-500 mt-2">التاريخ: {new Date()?.toLocaleDateString('ar-JO')}</p>
      </div>

      {/* Property Details */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">تفاصيل العقار</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">اسم العقار</p>
            <p className="text-base font-semibold text-gray-900">{contract?.propertyTitle}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">نوع العقار</p>
            <p className="text-base font-semibold text-gray-900">{contract?.propertyType}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">الموقع</p>
            <p className="text-base font-semibold text-gray-900">{contract?.location}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">المساحة</p>
            <p className="text-base font-semibold text-gray-900">{contract?.area}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">السعر</p>
            <p className="text-base font-semibold text-blue-600">{contract?.price}</p>
          </div>
        </div>
      </div>

      {/* Client Details */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">معلومات العميل</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">الاسم</p>
            <p className="text-base font-semibold text-gray-900">{contract?.clientName}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">رقم الهاتف</p>
            <p className="text-base font-semibold text-gray-900">{contract?.clientPhone}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">البريد الإلكتروني</p>
            <p className="text-base font-semibold text-gray-900">{contract?.clientEmail}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">رقم الهوية</p>
            <p className="text-base font-semibold text-gray-900">{contract?.clientId}</p>
          </div>
        </div>
      </div>

      {/* Contract Terms */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">شروط العقد</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">تاريخ البدء</p>
            <p className="text-base font-semibold text-gray-900">{contract?.startDate}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">تاريخ الانتهاء</p>
            <p className="text-base font-semibold text-gray-900">{contract?.endDate}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">طريقة الدفع</p>
            <p className="text-base font-semibold text-gray-900">{contract?.paymentMethod}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">التأمين</p>
            <p className="text-base font-semibold text-gray-900">{contract?.deposit}</p>
          </div>
        </div>
      </div>

      {/* Notes */}
      {contract?.notes && (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">ملاحظات</h2>
          <p className="text-base text-gray-700 leading-relaxed">{contract?.notes}</p>
        </div>
      )}

      {/* Signatures */}
      <div className="mt-12 pt-8 border-t-2 border-gray-300">
        <div className="grid grid-cols-2 gap-8">
          <div className="text-center">
            <div className="h-20 border-b-2 border-gray-400 mb-2"></div>
            <p className="text-sm font-semibold text-gray-900">توقيع الطرف الأول</p>
            <p className="text-xs text-gray-600 mt-1">التاريخ: _______________</p>
          </div>
          <div className="text-center">
            <div className="h-20 border-b-2 border-gray-400 mb-2"></div>
            <p className="text-sm font-semibold text-gray-900">توقيع الطرف الثاني</p>
            <p className="text-xs text-gray-600 mt-1">التاريخ: _______________</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMarketingContract = () => (
    <div className="contract-content" dir="rtl">
      {/* Header */}
      <div className="text-center mb-8 pb-6 border-b-2 border-gray-300">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">عقد تسويق عقاري</h1>
        <p className="text-lg text-gray-600">رقم العقد: {contract?.id || 'N/A'}</p>
        <p className="text-sm text-gray-500 mt-2">التاريخ: {new Date(contract?.created_at || Date.now())?.toLocaleDateString('ar-JO')}</p>
      </div>

      {/* Property Details */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">تفاصيل العقار</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">رقم العقار</p>
            <p className="text-base font-semibold text-gray-900">{contract?.property_id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">نوع العقار</p>
            <p className="text-base font-semibold text-gray-900">{contract?.property_type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">الموقع</p>
            <p className="text-base font-semibold text-gray-900">{contract?.location}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">السعر المطلوب</p>
            <p className="text-base font-semibold text-blue-600">{contract?.asking_price?.toLocaleString('ar-JO')} درهم</p>
          </div>
        </div>
      </div>

      {/* Owner Details */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">معلومات المالك</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">الاسم</p>
            <p className="text-base font-semibold text-gray-900">{contract?.owner_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">رقم الهاتف</p>
            <p className="text-base font-semibold text-gray-900">{contract?.owner_phone}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">البريد الإلكتروني</p>
            <p className="text-base font-semibold text-gray-900">{contract?.owner_email}</p>
          </div>
        </div>
      </div>

      {/* Marketing Terms */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">شروط التسويق</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">مدة العقد</p>
            <p className="text-base font-semibold text-gray-900">{contract?.contract_duration} شهر</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">نسبة العمولة</p>
            <p className="text-base font-semibold text-gray-900">{contract?.commission_percentage}%</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">مبلغ العمولة</p>
            <p className="text-base font-semibold text-blue-600">{contract?.commission_amount?.toLocaleString('ar-JO')} درهم</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">نوع الحصرية</p>
            <p className="text-base font-semibold text-gray-900">{contract?.exclusivity_type === 'exclusive' ? 'حصري' : 'غير حصري'}</p>
          </div>
        </div>
      </div>

      {/* Marketing Channels */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">قنوات التسويق</h2>
        <div className="flex flex-wrap gap-2">
          {contract?.marketing_channels?.map((channel, idx) => (
            <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {channel}
            </span>
          ))}
        </div>
      </div>

      {/* Approval Status */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">حالة الموافقات</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">موافقة ARDDJO</p>
            <p className="text-base font-semibold text-gray-900">{contract?.arddjo_acceptance_status === 'accepted' ? '✓ موافق' : contract?.arddjo_acceptance_status === 'rejected' ? '✗ مرفوض' : '⏳ قيد الانتظار'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">موافقة المالك</p>
            <p className="text-base font-semibold text-gray-900">{contract?.owner_acceptance_status === 'accepted' ? '✓ موافق' : contract?.owner_acceptance_status === 'rejected' ? '✗ مرفوض' : '⏳ قيد الانتظار'}</p>
          </div>
        </div>
      </div>

      {/* Generated Contract HTML */}
      {contract?.generated_contract && (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">نص العقد المُنشأ</h2>
          <div 
            className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: contract?.generated_contract }}
          />
        </div>
      )}

      {/* Signatures */}
      <div className="mt-12 pt-8 border-t-2 border-gray-300">
        <div className="grid grid-cols-3 gap-8">
          <div className="text-center">
            <div className="h-20 border-b-2 border-gray-400 mb-2"></div>
            <p className="text-sm font-semibold text-gray-900">توقيع ARDDJO</p>
            <p className="text-xs text-gray-600 mt-1">التاريخ: _______________</p>
          </div>
          <div className="text-center">
            <div className="h-20 border-b-2 border-gray-400 mb-2"></div>
            <p className="text-sm font-semibold text-gray-900">توقيع المالك</p>
            <p className="text-xs text-gray-600 mt-1">التاريخ: _______________</p>
          </div>
          <div className="text-center">
            <div className="h-20 border-b-2 border-gray-400 mb-2"></div>
            <p className="text-sm font-semibold text-gray-900">الشاهد</p>
            <p className="text-xs text-gray-600 mt-1">التاريخ: _______________</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBuyerRepresentationContract = () => (
    <div className="contract-content" dir="rtl">
      {/* Header */}
      <div className="text-center mb-8 pb-6 border-b-2 border-gray-300">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">عقد تمثيل مشتري عقاري</h1>
        <p className="text-lg text-gray-600">رقم العقد: {contract?.id || 'N/A'}</p>
        <p className="text-sm text-gray-500 mt-2">التاريخ: {new Date(contract?.created_at || Date.now())?.toLocaleDateString('ar-JO')}</p>
      </div>

      {/* Client Details */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">معلومات العميل</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">الاسم</p>
            <p className="text-base font-semibold text-gray-900">{contract?.client_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">رقم الهاتف</p>
            <p className="text-base font-semibold text-gray-900">{contract?.client_phone}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">البريد الإلكتروني</p>
            <p className="text-base font-semibold text-gray-900">{contract?.client_email}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">رقم الهوية</p>
            <p className="text-base font-semibold text-gray-900">{contract?.client_id_number}</p>
          </div>
        </div>
      </div>

      {/* Property Preferences */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">تفضيلات العقار</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">نوع العقار</p>
            <p className="text-base font-semibold text-gray-900">{contract?.property_type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">الميزانية</p>
            <p className="text-base font-semibold text-blue-600">{contract?.budget_min?.toLocaleString('ar-JO')} - {contract?.budget_max?.toLocaleString('ar-JO')} درهم</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">عدد الغرف</p>
            <p className="text-base font-semibold text-gray-900">{contract?.bedrooms_min} - {contract?.bedrooms_max}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">عدد الحمامات</p>
            <p className="text-base font-semibold text-gray-900">{contract?.bathrooms_min} - {contract?.bathrooms_max}</p>
          </div>
        </div>
      </div>

      {/* Preferred Locations */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">المواقع المفضلة</h2>
        <div className="flex flex-wrap gap-2">
          {contract?.preferred_locations?.map((location, idx) => (
            <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              {location}
            </span>
          ))}
        </div>
      </div>

      {/* Contract Terms */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">شروط العقد</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">مدة العقد</p>
            <p className="text-base font-semibold text-gray-900">{contract?.contract_duration} شهر</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">نسبة العمولة</p>
            <p className="text-base font-semibold text-gray-900">{contract?.commission_percentage}%</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">نوع الحصرية</p>
            <p className="text-base font-semibold text-gray-900">{contract?.exclusivity_type === 'exclusive' ? 'حصري' : 'غير حصري'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">تاريخ البدء</p>
            <p className="text-base font-semibold text-gray-900">{new Date(contract?.start_date)?.toLocaleDateString('ar-JO')}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">تاريخ الانتهاء</p>
            <p className="text-base font-semibold text-gray-900">{new Date(contract?.end_date)?.toLocaleDateString('ar-JO')}</p>
          </div>
        </div>
      </div>

      {/* Approval Status */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">حالة الموافقات</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">موافقة ARDDJO</p>
            <p className="text-base font-semibold text-gray-900">{contract?.arddjo_acceptance_status === 'accepted' ? '✓ موافق' : contract?.arddjo_acceptance_status === 'rejected' ? '✗ مرفوض' : '⏳ قيد الانتظار'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">موافقة العميل</p>
            <p className="text-base font-semibold text-gray-900">{contract?.client_acceptance_status === 'accepted' ? '✓ موافق' : contract?.client_acceptance_status === 'rejected' ? '✗ مرفوض' : '⏳ قيد الانتظار'}</p>
          </div>
        </div>
      </div>

      {/* Generated Contract HTML */}
      {contract?.generated_contract && (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-300">نص العقد المُنشأ</h2>
          <div 
            className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: contract?.generated_contract }}
          />
        </div>
      )}

      {/* Signatures */}
      <div className="mt-12 pt-8 border-t-2 border-gray-300">
        <div className="grid grid-cols-3 gap-8">
          <div className="text-center">
            <div className="h-20 border-b-2 border-gray-400 mb-2"></div>
            <p className="text-sm font-semibold text-gray-900">توقيع ARDDJO</p>
            <p className="text-xs text-gray-600 mt-1">التاريخ: _______________</p>
          </div>
          <div className="text-center">
            <div className="h-20 border-b-2 border-gray-400 mb-2"></div>
            <p className="text-sm font-semibold text-gray-900">توقيع العميل</p>
            <p className="text-xs text-gray-600 mt-1">التاريخ: _______________</p>
          </div>
          <div className="text-center">
            <div className="h-20 border-b-2 border-gray-400 mb-2"></div>
            <p className="text-sm font-semibold text-gray-900">الشاهد</p>
            <p className="text-xs text-gray-600 mt-1">التاريخ: _______________</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContract = () => {
    switch (contractType) {
      case 'marketing':
        return renderMarketingContract();
      case 'buyer_representation':
        return renderBuyerRepresentationContract();
      default:
        return renderRegularContract();
    }
  };

  if (!contract) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="full" title="" footer={null} icon={null} iconColor={null}>
        <div className="flex flex-col h-full">
          {/* Header - Hidden when printing */}
          <div className="flex items-center justify-between p-4 border-b border-border no-print">
            <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
              <Icon name="FileText" size={24} />
              معاينة العقد
            </h2>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleDownloadPDF}
                variant="outline"
                size="sm"
                disabled={isPrinting}
              >
                <Icon name="Download" size={16} className="ml-2" />
                تحميل PDF
              </Button>
              <Button
                onClick={handlePrint}
                variant="default"
                size="sm"
                disabled={isPrinting}
              >
                <Icon name={isPrinting ? 'Loader2' : 'Printer'} size={16} className={`ml-2 ${isPrinting ? 'animate-spin' : ''}`} />
                {isPrinting ? 'جاري الطباعة...' : 'طباعة'}
              </Button>
              <Button
                onClick={onClose}
                variant="ghost"
                size="sm"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Contract Content */}
          <div className="flex-1 overflow-y-auto p-8 bg-white" ref={printRef}>
            <div className="max-w-4xl mx-auto bg-white">
              {renderContract()}
            </div>
          </div>
        </div>
      </Modal>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          
          .contract-content,
          .contract-content * {
            visibility: visible;
          }
          
          .contract-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20mm;
          }
          
          .no-print {
            display: none !important;
          }
          
          /* Page breaks */
          h2 {
            page-break-after: avoid;
          }
          
          .grid {
            page-break-inside: avoid;
          }
          
          /* Print-specific styling */
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </>
  );
};

export default ContractPreviewModal;